import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Disc3 } from "lucide-react";

const SIZE = 260;
const RADIUS = SIZE / 2;

export function GameShowWheelDial({ show, isHost }) {
  const wedges = show.wheel?.wedges || [];
  const spinState = show.wheel?.spin_state || {};
  const [rotation, setRotation] = useState(0);
  const [spinTarget, setSpinTarget] = useState("");
  const [spinning, setSpinning] = useState(false);
  const lastSpunAt = useRef(null);

  useEffect(() => {
    if (!spinState.spun_at || spinState.spun_at === lastSpunAt.current) return;
    lastSpunAt.current = spinState.spun_at;
    const finalMod = ((spinState.rotation_deg % 360) + 360) % 360;
    setRotation((prev) => {
      const prevMod = ((prev % 360) + 360) % 360;
      let delta = finalMod - prevMod;
      if (delta <= 0) delta += 360;
      return prev + delta + 4 * 360;
    });
    setSpinning(true);
    const t = setTimeout(() => setSpinning(false), 3200);
    return () => clearTimeout(t);
  }, [spinState.spun_at, spinState.rotation_deg]);

  const spin = async () => {
    if (!spinTarget) return toast.error("Pick who's spinning first");
    try {
      await api.post(`/game-shows/${show.id}/wheel/spin`, { participant_id: spinTarget });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not spin");
    }
  };

  const resultWedge = wedges.find((w) => w.id === spinState.result_wedge_id);
  const n = wedges.length || 1;
  const sliceDeg = 360 / n;
  const gradient = wedges.length
    ? `conic-gradient(${wedges.map((w, i) => `${w.color || "#D4AF37"} ${i * sliceDeg}deg ${(i + 1) * sliceDeg}deg`).join(", ")})`
    : "#2a2118";

  return (
    <div className="flex flex-col items-center gap-4" data-testid="gameshow-wheel-dial">
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-[#D90429]" data-testid="gameshow-wheel-pointer" />
        <div
          className="rounded-full border-4 border-[#D4AF37]/60 shadow-2xl relative overflow-hidden"
          style={{ width: SIZE, height: SIZE, background: gradient, transform: `rotate(${rotation}deg)`, transition: !spinning ? "none" : "transform 3s cubic-bezier(0.15,0.65,0.15,1)" }}
        >
          {wedges.map((w, i) => {
            const angle = i * sliceDeg + sliceDeg / 2;
            return (
              <div key={w.id} className="absolute top-1/2 left-1/2" style={{ transform: `rotate(${angle}deg)` }}>
                <span
                  className="absolute text-[10px] font-semibold text-white/90 whitespace-nowrap"
                  style={{ left: 6, top: -6, transform: `translateX(${RADIUS - 34}px) rotate(90deg)` }}
                >
                  {w.label}
                </span>
              </div>
            );
          })}
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-8 h-8 rounded-full bg-[#151613] border-2 border-[#D4AF37]" />
        </div>
      </div>
      {wedges.length === 0 && <p className="text-xs text-[#f1e0c5]/30">{isHost ? "Add wedges below to build the wheel." : "The host hasn't built the wheel yet."}</p>}
      {resultWedge && !spinning && (
        <p className="text-sm text-[#D4AF37] font-display text-lg" data-testid="gameshow-wheel-last-result">Landed on: {resultWedge.label}</p>
      )}
      {isHost && wedges.length > 0 && (
        <div className="flex gap-2 w-full max-w-xs">
          <Select value={spinTarget} onValueChange={setSpinTarget}>
            <SelectTrigger data-testid="gameshow-spin-target-select" className="bg-black/30 border-white/10 flex-1"><SelectValue placeholder="Who's spinning?" /></SelectTrigger>
            <SelectContent>
              {(show.participants || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="gameshow-spin-button" disabled={spinning} onClick={spin} className="rounded-full bg-[#3F9142] hover:bg-[#357a39] gap-1.5">
            <Disc3 className="w-4 h-4" /> Spin
          </Button>
        </div>
      )}
    </div>
  );
}
